import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TABS, type TabKey } from './TabBar';

/**
 * The phone's navigation: four sections along the bottom, the rest behind More.
 *
 * Eight tabs never fit in 390px. They used to live in a row that scrolled
 * sideways, which works but reads as a web page — the sections you have not
 * scrolled to are invisible, and a horizontal scroll inside a vertical page is
 * the thing phones are worst at. Fixed to the bottom, the whole app is one thumb
 * reach away and always visible, which is the single change that makes this feel
 * like an app rather than a site.
 *
 * **Four, not eight.** A row of eight 44px targets in 390px leaves 4px of label,
 * so the ones that matter day to day are out and the rest are one tap deeper.
 * Home, Guests, Budget and Timeline are the four that get opened repeatedly;
 * venues, vendors, seating and the public site are decided once and revisited.
 *
 * Hidden from `sm` up, where `TabBar` is already the right shape.
 */

const PRIMARY: TabKey[] = ['home', 'guests', 'budget', 'timeline'];
const SECONDARY: TabKey[] = TABS.filter((t) => !PRIMARY.includes(t));

/** Line art, so a tab reads at a glance and still prints as one colour. */
const ICONS: Record<TabKey, string> = {
  home: 'M3 9.5 10 4l7 5.5V16a1 1 0 0 1-1 1h-3v-4H7v4H4a1 1 0 0 1-1-1z',
  guests:
    'M7 9a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5zM2 17c0-2.8 2.2-5 5-5s5 2.2 5 5M14 9.5a2 2 0 1 0 0-4M18 17c0-2.2-1.4-4-3.5-4.6',
  venues: 'M3 17V8l7-4 7 4v9M8 17v-4h4v4',
  vendors: 'M4 7h12v10H4zM7 7V5h6v2',
  budget: 'M10 3v14M6.5 6.5h5a2 2 0 0 1 0 4h-3a2 2 0 0 0 0 4h5',
  seating: 'M5 8V5h10v3M3 8h14v4H3zM5 12v4M15 12v4',
  timeline: 'M10 5v5l3 2M10 17a7 7 0 1 1 0-14 7 7 0 0 1 0 14z',
  site: 'M10 17a7 7 0 1 1 0-14 7 7 0 0 1 0 14zM3 10h14M10 3c2 2.5 2 11.5 0 14M10 3c-2 2.5-2 11.5 0 14',
};

function Icon({ tab }: { tab: TabKey }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 20 20" className="size-5 shrink-0">
      <path
        d={ICONS[tab]}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const linkClass = ({ isActive }: { isActive: boolean }) =>
  `flex min-h-tap flex-1 flex-col items-center justify-center gap-0.5 px-1 py-1.5 text-[11px] leading-none ${
    isActive ? 'text-leaf' : 'text-leaf-soft'
  }`;

export function BottomNav({ wedId }: { wedId: string }) {
  const { t } = useTranslation();
  const [more, setMore] = useState(false);

  return (
    <>
      {more && (
        <>
          <div
            aria-hidden="true"
            onPointerDown={() => setMore(false)}
            className="fixed inset-0 z-40 bg-ink/20 sm:hidden"
          />
          <div
            className="panel fixed inset-x-0 bottom-0 z-50 rounded-t pb-[max(0.5rem,env(safe-area-inset-bottom))] sm:hidden"
            role="menu"
          >
            {SECONDARY.map((tab) => (
              <NavLink
                key={tab}
                to={`/app/w/${wedId}/${tab}`}
                role="menuitem"
                onClick={() => setMore(false)}
                className="flex min-h-tap items-center gap-3 px-4 text-body text-ink"
              >
                <Icon tab={tab} />
                {t(`nav.${tab}`)}
              </NavLink>
            ))}
          </div>
        </>
      )}

      {/* `pb` for the home indicator on a notched phone, so the last row of
          labels is not sitting under it. */}
      <nav
        aria-label={t('nav.sections')}
        className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-paper pb-[env(safe-area-inset-bottom)] sm:hidden"
      >
        <div className="flex items-stretch">
          {PRIMARY.map((tab) => (
            <NavLink key={tab} to={`/app/w/${wedId}/${tab}`} className={linkClass}>
              {({ isActive }) => (
                <>
                  <Icon tab={tab} />
                  <span className={isActive ? 'font-medium' : ''}>{t(`nav.${tab}`)}</span>
                </>
              )}
            </NavLink>
          ))}
          <button
            type="button"
            aria-expanded={more}
            onClick={() => setMore((v) => !v)}
            className={`flex min-h-tap flex-1 flex-col items-center justify-center gap-0.5 px-1 py-1.5 text-[11px] leading-none ${
              more ? 'text-leaf' : 'text-leaf-soft'
            }`}
          >
            <svg aria-hidden="true" viewBox="0 0 20 20" className="size-5 shrink-0">
              <circle cx="4" cy="10" r="1.4" fill="currentColor" />
              <circle cx="10" cy="10" r="1.4" fill="currentColor" />
              <circle cx="16" cy="10" r="1.4" fill="currentColor" />
            </svg>
            <span>{t('nav.more')}</span>
          </button>
        </div>
      </nav>
    </>
  );
}
